/**
 * <queue-banner>: keeps the store's open/closed banner in sync with the
 * break queue. Settings come from data- attributes set in
 * blocks/queue-banner.liquid.
 *
 * - Checks data-status-url, which answers { "open": true | false }.
 * - Checks again every data-refresh seconds while the tab is visible,
 *   and straight away when someone comes back to the tab.
 * - data-show-when: "always", "closed" (only when closed) or "open".
 * - If the status can't be read, the banner hides instead of guessing.
 * - data-preview (theme editor only): "open" or "closed" forces a state
 *   so it can be styled without opening or closing the real queue.
 */
if (!customElements.get('queue-banner')) {
  class QueueBanner extends HTMLElement {
    connectedCallback() {
      this.statusUrl = this.dataset.statusUrl;
      this.showWhen = this.dataset.showWhen || 'always';
      this.refreshMs = Math.max(10, Number(this.dataset.refresh) || 20) * 1000;
      this.lastOpen = null;

      const preview = this.dataset.preview;
      if (preview === 'open' || preview === 'closed') {
        this.show(preview === 'open');
        return;
      }

      this.onVisible = () => {
        if (document.visibilityState === 'visible') this.check();
      };
      document.addEventListener('visibilitychange', this.onVisible);
      this.check();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      if (this.onVisible) document.removeEventListener('visibilitychange', this.onVisible);
    }

    async check() {
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);

      try {
        // no-store: skip the browser's copy (the status sends max-age=15),
        // so a change shows up on the next check instead of up to 15s later.
        const res = await fetch(this.statusUrl, {
          signal: controller.signal,
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Status ' + res.status);
        const data = await res.json();
        if (typeof data.open !== 'boolean') throw new Error('Unexpected answer');
        if (this.controller === controller) this.show(data.open);
      } catch (err) {
        // A newer check replaced this one, or the banner was removed: ignore.
        if (this.controller === controller && this.isConnected) this.fail();
      } finally {
        clearTimeout(timeout);
        const current = this.controller === controller;
        if (current && this.isConnected && document.visibilityState === 'visible') {
          this.timer = setTimeout(() => this.check(), this.refreshMs);
        }
      }
    }

    show(open) {
      const wanted = this.showWhen === 'always' || (this.showWhen === 'open') === open;
      this.dataset.state = open ? 'open' : 'closed';
      this.hidden = !wanted;

      // Announce changes after the first load, not on every page view.
      const inner = this.querySelector('[role="status"]');
      if (inner) inner.setAttribute('aria-live', this.lastOpen === null ? 'off' : 'polite');
      this.lastOpen = open;
    }

    fail() {
      // Keep showing the last known state if there was one; otherwise stay hidden.
      if (this.lastOpen === null) {
        this.dataset.state = 'error';
        this.hidden = true;
      }
    }
  }

  customElements.define('queue-banner', QueueBanner);
}
